import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Typography, Box, Grid } from '@mui/material';
import {
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
  Fade
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';

const AboutPage = () => {
    return (
        <>
            <Fade in timeout={800}>
                <Container sx={{ py: 4 }}>
                    <Typography variant="h4" gutterBottom>
                    Shop Smart. Ship Fast. Stay Canadian.
                    </Typography>
                    <Typography paragraph>
                    At CCOO.ca, we believe great products should be easy to find, fast to ship, and proudly Canadian.
                    Based in Vancouver, we curate high-quality essentials and standout finds — all sold exclusively through Amazon.
                    </Typography>
                    <Typography paragraph>
                    Whether it’s home goods, lifestyle accessories, or seasonal must-haves, our mission is simple:
                    deliver value, style, and trust with every order. We handle the sourcing, branding, and fulfillment
                    so you can shop confidently, knowing your purchase is backed by Amazon’s reliability and our Canadian commitment.
                    </Typography>
                    <Typography paragraph>
                    We’re not just another storefront. We’re a growing community of makers, curators, and customers
                    who care about quality, simplicity, and national pride.
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    <Typography variant="h5" gutterBottom>
                    🇨🇦 Why CCOO?
                    </Typography>
                    <List>
                    {[
                        'Canadian roots — designed and curated in Canada',
                        'Amazon fulfillment — fast, secure, and trusted delivery',
                        'Curated catalog — only products we believe in',
                        'Customer-first — easy support, clear policies, no fluff'
                    ].map((text, index) => (
                        <ListItem key={index}>
                        <ListItemIcon>
                            <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary={text} />
                        </ListItem>
                    ))}
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography variant="h5" gutterBottom>
                    📖 Our Story
                    </Typography>
                    <Typography paragraph>
                    CCOO.ca started with a simple idea: make online shopping feel local, even when it’s global.
                    As Canadian sellers, we saw a gap — great products were getting lost in the noise of big marketplaces.
                    So we built a brand that puts quality first, with a clean identity and a clear promise:
                    smart shopping, fast shipping, and a proudly Canadian experience.
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    <Typography variant="h5" gutterBottom>
                    🕰️ Timeline
                    </Typography>
                    <List>
                    {[
                        '2023 — CCOO.ca concept born in Vancouver',
                        '2024 — First product launched on Amazon.ca',
                        '2025 — Brand identity finalized — logo, tagline, and storefront',
                        '2025 — Website launched as a curated catalog and brand hub',
                        '2026 — Expansion into seasonal collections and lifestyle categories'
                    ].map((event, index) => (
                        <ListItem key={index}>
                        <ListItemIcon>
                            <AccessTimeIcon color="info" />
                        </ListItemIcon>
                        <ListItemText primary={event} />
                        </ListItem>
                    ))}
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography variant="h5" gutterBottom>
                    🖼️ Visual Highlights
                    </Typography>
                    <List>
                    {[
                        '✅ Logo: Bold, modern, and unmistakably Canadian — featuring the iconic maple leaf',
                        '🛍️ Shopping Bag Icon: Represents trust, simplicity, and the joy of smart shopping',
                        '🔗 Amazon-first: Every product links directly to Amazon.ca for fast, secure checkout',
                        '🌐 Website: A clean, curated catalog that feels like a boutique storefront'
                    ].map((highlight, index) => (
                        <ListItem key={index}>
                        <ListItemText primary={highlight} />
                        </ListItem>
                    ))}
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography variant="h5" gutterBottom>
                    🛍️ Where to Shop
                    </Typography>
                    <Typography paragraph>
                    All our products are also available on{' '}
                    <Link href="https://www.amazon.ca" target="_blank" rel="noopener">
                        Amazon.ca
                    </Link>
                    . You can browse them right here on our site, then click through to purchase with confidence.
                    </Typography>
                </Container>
            </Fade>
        </>
    )
}
export default AboutPage;